import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, retry, throwError, timer } from 'rxjs';
import { AuthService } from '../services/auth.service';

/** How long to wait before re-sending a read the server refused with a 403. */
export const FORBIDDEN_RETRY_DELAY_MS = 600;

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  // The API intermittently answers a read with 403 "Required permissions: …"
  // for an admin who holds that permission — the same request succeeds a
  // moment later, which is why a page refresh "fixes" it. Until the server is
  // fixed, a GET refused that way is sent once more before the error reaches
  // the screen. A real denial costs one extra request and still shows. Writes
  // are never re-sent.
  const isRead = req.method === 'GET' || req.method === 'HEAD';

  return next(req).pipe(
    retry({
      count: isRead ? 1 : 0,
      delay: (err: HttpErrorResponse) =>
        err.status === 403 ? timer(FORBIDDEN_RETRY_DELAY_MS) : throwError(() => err),
    }),
    catchError((err: HttpErrorResponse) => {
      // 401 — token expired or invalid → log out
      if (err.status === 401) {
        auth.clearSession();
        return throwError(() => err);
      }

      // 500 — ONLY log out if it's specifically the Laravel auth redirect error
      // ("Route [login] not defined") — NOT for any other 500 like validation,
      // unexpected fields, server errors, etc.
      if (err.status === 500) {
        const msg: string   = err.error?.message ?? '';
        const trace: string = err.error?.errors?.trace ?? '';
        const isAuthRedirect =
          msg === 'Route [login] not defined.' ||
          (trace.includes('Authenticate.php') && trace.includes('route()'));

        if (isAuthRedirect) {
          auth.clearSession();
        }
        // All other 500s: just pass through — let the component handle it
      }

      return throwError(() => err);
    })
  );
};
